import React, { useEffect, useMemo, useState } from "react";

// Quick, one-shot confetti burst. Renders a ring of emoji pieces that shoot
// outward from the centre of the parent (which must be `position: relative`),
// then unmounts itself after `duration`ms. Purely cosmetic — pointer-events
// disabled so it never blocks interactions beneath.
const PIECES = ["🎉", "🎊", "🎈", "🎂", "🧁", "⭐️", "✨", "🎁"];

export default function ConfettiBurst({ duration = 1100, onDone }) {
  const [visible, setVisible] = useState(true);

  const pieces = useMemo(() => {
    const count = 18;
    return Array.from({ length: count }).map((_, i) => {
      const angle = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
      const distance = 80 + Math.random() * 60; // px
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance;
      return {
        key: `p${i}`,
        emoji: PIECES[i % PIECES.length],
        dx: Math.round(dx),
        dy: Math.round(dy),
        rotate: Math.round(Math.random() * 720 - 360),
        size: 18 + Math.round(Math.random() * 14),
        delay: Math.round(Math.random() * 80),
      };
    });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setVisible(false);
      onDone && onDone();
    }, duration);
    return () => clearTimeout(t);
  }, [duration, onDone]);

  if (!visible) return null;

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-visible z-10"
      aria-hidden="true"
      data-testid="confetti-burst"
    >
      <div className="absolute left-1/2 top-1/2">
        {pieces.map((p) => (
          <span
            key={p.key}
            className="absolute ff-confetti-piece"
            style={{
              left: 0,
              top: 0,
              fontSize: `${p.size}px`,
              animationDuration: `${duration}ms`,
              animationDelay: `${p.delay}ms`,
              ["--dx"]: `${p.dx}px`,
              ["--dy"]: `${p.dy}px`,
              ["--spin"]: `${p.rotate}deg`,
            }}
          >
            {p.emoji}
          </span>
        ))}
      </div>
      <style>{`
        @keyframes ff-confetti-burst {
          0%   { transform: translate3d(-50%, -50%, 0) scale(0.4); opacity: 0; }
          20%  { transform: translate3d(calc(-50% + (var(--dx) * 0.35)), calc(-50% + (var(--dy) * 0.35)), 0) scale(1); opacity: 1; }
          100% { transform: translate3d(calc(-50% + var(--dx)), calc(-50% + var(--dy) + 40px), 0) rotate(var(--spin, 360deg)) scale(0.85); opacity: 0; }
        }
        .ff-confetti-piece {
          animation-name: ff-confetti-burst;
          animation-timing-function: cubic-bezier(0.2, 0.7, 0.3, 1);
          animation-fill-mode: forwards;
          will-change: transform, opacity;
        }
      `}</style>
    </div>
  );
}
